import getCurrentCamera from 'helpers/cameras/getCurrentCamera'
import { endingWiresSelector, doorControllerDoorIdsSelector, doorControllerExpansionIdsSelector } from './components'
import getWireDistance from 'helpers/wires/getWireDistance'
import flatten from 'ramda/src/flatten'

export function currentProjectTitle(state) {
  const title = state.projects
                  .find(p => p.id === state.currentProjectMetadata._id)
                  .title
  return { title }
}

export function projectsSelector(state) {
  const { projects } = state
  return { projects }
}

export function camerasSelector(state) {
  const { cameras } = state
  return { cameras }
}

export function cameraIdsSelector(state) {
  const { cameras } = state
  return { cameraIds: cameras.map(c => c.id) }
}

export function floorPlanIdsSelector(state) {
  const { map: { floorPlans } } = state
  return { floorPlanIds: floorPlans.map(f => f.id) }
}

export function currentProjectIdSelector(state) {
  const { currentProjectMetadata: { _id } } = state
  return { currentProjectId: _id }
}

export function currentCameraSelector({ cameras }) {
  const currentCamera = getCurrentCamera(cameras)
  return { currentCamera }
}

export function mapSelector(state) {
  const { map } = state
  return { map }
}

export function cameraModelsSelector(state) {
  const { cameraModels } = state
  return { cameraModels }
}

export function doorControllerModelsSelector(state) {
  const { doorControllerModels } = state
  return { doorControllerModels }
}

export function expansionControllerModelsSelector(state) {
  const { expansionControllerModels } = state
  return { expansionControllerModels }
}

export function floorPlansLoadedSelector(state) {
  const { floorPlansLoaded, map: { floorPlans } } = state
  return { floorPlansLoaded, floorPlans }
}

export function currentEntitySelector(state) {
  const { currentEntity } = state
  return { currentEntity }
}

export function lockedSelector(state) {
  const { locked } = state
  return { locked }
}

export function uiSelector(state) {
  const { ui } = state
  return { ui }
}

export function cameraFiltersSelector(state) {
  const { cameraFilters } = state
  return { cameraFilters }
}

export function offlineSelector(state) {
  const { offline } = state
  return { offline }
}

export function activeSelector(state) {
  const { active } = state
  return { active }
}

export function manufacturerSpecificSelector(state) {
  const { manufacturerSpecific } = state
  return { manufacturerSpecific }
}

export function databaseSelector(state) {
  const { database } = state
  return { database }
}

export function cameraDataFilenameSelector(state) {
  const { cameraDataFilename } = state
  return { cameraDataFilename }
}

export function exportsSelector(state) {
  const { exports } = state
  return { exports }
}

export function networkClosetsSelector(state) {
  return { networkClosets: state.entities.networkClosets.byId }
}

export function networkClosetCountSelector(state) {
  return { networkClosetCount: state.entities.networkClosets.allIds.length }
}

export function wiredDevicesSelector(state) {
  return { wiredDevices: state.entities.wiredDevices.byId }
}

export function wiredDeviceCountSelector(state) {
  return { wiredDeviceCount: state.entities.wiredDevices.allIds.length }
}

export function doorControllersSelector(state) {
  return { doorControllers: state.entities.doorControllers.byId }
}

export function doorControllerCountSelector(state) {
  return { doorControllerCount: state.entities.doorControllers.allIds.length }
}

export function devicesSelector(state) {
  const { cameras, entities: { networkClosets, wiredDevices, doorControllers, doors } } = state

  return { devices: cameras.concat(Object.values(networkClosets.byId), Object.values(wiredDevices.byId), Object.values(doorControllers.byId), Object.values(doors.byId)) }
}

// SAGA SELECTORS

export function mergePermalinkProjectSelector(state) {
  const { cameras, map: { location, zoom, mapType, floorPlans, markups }, ui, createdAt, updatedAt } = state
  return { mapLocation: location, floorPlans, markups, cameras, zoom, ui, mapType,
    ...currentProjectTitle(state), createdAt, updatedAt, ...mapSelector(state) }
}

export function updateProjectSelector(state) {
  const { cameras, currentEntity, map: { location, zoom, mapType, floorPlans, markups }, ui, createdAt, updatedAt, entities } = state
  return { currentEntity, mapLocation: location, floorPlans, markups, cameras, zoom, ui, mapType,
    ...currentProjectIdSelector(state), ...currentProjectTitle(state), createdAt, updatedAt, entities }
}

export function autosaveSelector(state) {
  const { cameras, currentEntity, map, ui, createdAt, updatedAt, currentProjectMetadata, projects,
    favoriteModels, recentModels, favoriteDoorControllerModels, recentDoorControllerModels, userSettings, entities } = state

  return { currentEntity, map, cameras, ui, currentProjectMetadata, projects, createdAt, updatedAt, favoriteModels, recentModels,
    favoriteDoorControllerModels, recentDoorControllerModels, userSettings, entities }
}

export function createCameraSelector(state) {
  const { map: { ref }, ui: { titleChanged, isImperial } } = state
  return { map: ref, ...cameraIdsSelector(state), titleChanged, isImperial, ...devicesSelector(state) }
}

export function createFloorPlanSelector(state) {
  const { map: { ref } } = state
  return { map: ref, ...floorPlanIdsSelector(state) }
}

export function removeFloorPlanSelector(state) {
  const { map: { floorPlans }, ui: { floorPlanScaleID } } = state
  return { floorPlans, floorPlanScaleID }
}

export function destroyCameraSelector(state) {
  return { ...camerasSelector(state), ...cameraIdsSelector(state) }
}

export function changeProjectSelector(state) {
  return { ...createProjectSelector(state), ...camerasSelector(state), ...databaseSelector(state) }
}

export function createProjectSelector(state) {
  const { map, permalink } = state
  return { map, permalink, ...camerasSelector(state), ...projectsSelector(state), ...manufacturerSpecificSelector(state) }
}

export function addCameraToMapSelector(state) {
  return { ...camerasSelector(state), ...currentCameraSelector(state) }
}

export function destroyProjectsSelector(state) {
  const { map } = state
  return { map, ...projectsSelector(state), ...currentProjectIdSelector(state), ...camerasSelector(state), ...databaseSelector(state) }
}

export function exportSelector(state) {
  return { ...camerasSelector(state) }
}

export function startExportProcessSelector(state) {
  const { exportType } = state
  return { ...camerasSelector(state), ...mapSelector(state), exportType }
}

export function exportDoDevicesSelector(state) {
  const { entities: { doorControllers, doors } } = state

  return { doorControllers: doorControllers.byId, doors: doors.byId, ...networkClosetsSelector(state), ...wiredDevicesSelector(state), ...currentEntitySelector(state) }
}

export function zoomToAllDevicesSelector(state) {
  const { entities: { doorControllers, doors, networkClosets, wiredDevices } } = state

  return {
    ...camerasSelector(state),
    ...mapSelector(state),
    networkClosets: Object.values(networkClosets.byId),
    wiredDevices: Object.values(wiredDevices.byId),
    doorControllers: Object.values(doorControllers.byId),
    doors: Object.values(doors.byId)
  }
}

export function loginAndRestartAppSelector(state) {
  return { ...mapSelector(state), ...updateProjectSelector(state),
    ...manufacturerSpecificSelector(state), ...cameraDataFilenameSelector(state) }
}

export function copyCameraSelector(state) {
  const { map: { ref }, ui: { isImperial } } = state
  return { ...camerasSelector(state), map: ref, isImperial, ...devicesSelector(state) }
}

export function getAndApplyCameraDataSelector(state) {
  return { ...cameraModelsSelector(state), ...cameraFiltersSelector(state), ...doorControllerModelsSelector(state), ...expansionControllerModelsSelector(state) }
}

export function resetSpecificManufacturersAndRestartAppSelector(state) {
  return { ...mapSelector(state), ...updateProjectSelector(state),
    ...offlineSelector(state), ...cameraDataFilenameSelector(state),
    ...activeSelector(state) }
}

export function updateDocumentSelector(state) {
  return { ...databaseSelector(state), ...state.currentProjectMetadata }
}

export function compareUpdatedAtSelector(state) {
  return {
    currentProjectUpdatedAt: state.updatedAt,
    ...databaseSelector(state),
    ...state.currentProjectMetadata
  }
}

export function scaleFloorPlanSelector(state) {
  const { map: { ref, floorPlans }, ui: { floorPlanScaleID } } = state
  const currentScaleFloorplan = floorPlans.find(f => f.id == floorPlanScaleID)

  if (currentScaleFloorplan) {
    return { map: ref, floorPlans, scaler: currentScaleFloorplan.scaler }
  } else {
    return { map: ref, floorPlans }
  }
}

export function cancelScaleFloorPlanSelector(state) {
  const { map: { floorPlans }, ui: { floorPlanScaleID } } = state
  const currentScaleFloorplan = floorPlans.find(f => f.id == floorPlanScaleID)

  return { id: floorPlanScaleID, scaler: currentScaleFloorplan && currentScaleFloorplan.scaler }
}

export function fovPreviewSelector(state) {
  const { fovReady } = state
  return { ...currentEntitySelector(state), fovReady }
}

export function selectProjectSelector(state) {
  const { showMobileStart } = state
  return { ...databaseSelector(state), showMobileStart, ...activeSelector(state) }
}

export function createMobileProjectSelector(state) {
  return { ...selectProjectSelector(state), ...offlineSelector(state), ...activeSelector(state) }
}

export function reverseGeocodeSelector(state) {
  const { ui: { titleChanged }, entities } = state
  return { ...camerasSelector(state), entities, titleChanged }
}

export function setMultiImagerSelector(state) {
  return { ...currentCameraSelector(state), ...cameraIdsSelector(state) }
}

export function changeCameraModelSelector(state) {
  return { ...currentCameraSelector(state), ...camerasSelector(state) }
}

export function setupCameraSelector(state) {
  return { ...currentEntitySelector(state), ...currentCameraSelector(state) }
}

export function updatePPFHoverSelector(state) {
  const { ui: { isImperial } } = state
  return { ...currentCameraSelector(state), isImperial }
}

export function zoomToNetworkClosetSelector(state) {
  const { cameras, entities: { wiredDevices, doorControllers } } = state
  const wires = endingWiresSelector(state)
  const deviceLocations = wires.map(wire => {
    switch (wire.start.type) {
      case 'camera':
        return cameras.find(c => c.id === wire.start.id).location
      case 'doorController':
        return doorControllers.byId[wire.start.id].location
      case 'wiredDevice':
        return wiredDevices.byId[wire.start.id].location
    }
  })

  return { ...mapSelector(state), deviceLocations }
}

export function zoomToDoorControllerSelector(state) {
  const { entities: { doors, doorControllers } } = state
  const doorIds = doorControllerDoorIdsSelector(state)
  const expansionIds = doorControllerExpansionIdsSelector(state)

  return { ...mapSelector(state), doorLocations: doorIds.map(doorId => doors.byId[doorId].location), expansionLocations: expansionIds.map(expansionId => doorControllers.byId[expansionId].location) }
}

export function setupNetworkClosetSelector(state) {
  return { deviceCount: endingWiresSelector(state).length }
}

export function compressProjectForExportsSelector(state, isCsv) {
  const cameras = state.cameras

  if (!isCsv) return { cameras }

  return {
    cameras: cameras.map(camera => {
      const wire = Object.values(state.entities.wires.byId).find(w => w.start.id === (camera.imagerId || camera.id) && w.start.type === 'camera')
      const networkCloset = wire && state.entities.networkClosets.byId[wire.end.id]

      return Object.assign({},
        camera,
        wire && {
          wireDistance: getWireDistance(wire.overlay, state.ui.isImperial, networkCloset.dropLength).rawDistance.toFixed(),
          networkClosetName: networkCloset.name
        }
      )
    })
  }
}

export function checkLocationSearchSelector(state) {
  const { cameras, map: { floorPlans, markups }, entities, offline } = state

  return {
    projectEdited: cameras.length || floorPlans.length || markups.length || ['doorControllers', 'doors', 'networkClosets', 'wiredDevices', 'wires'].some(type => entities[type].allIds.length),
    offline
  }
}

export function checkOfflineLimitsSelector(state) {
  const { map: { ref }, ui: { isImperial, titleChanged } } = state

  return { map: ref, isImperial, titleChanged, ...offlineSelector(state) }
}



// WEBPACK FOOTER //
// ./src/selectors/index.js